import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CardShell from '../../components/CardShell';
import { RiUploadLine, RiArrowRightUpLine } from '@remixicon/react';
import { ReportNavContext } from '../../components/reportNavContext';
import RealiseImportDialog from './RealiseImportDialog';
import { ReportingContext } from '../../components/ReportingControls';
import { useP } from '../../i18n/useP';
import { usePersistentValueLocal } from '../../hooks/usePersistentState';
import { OfferIcon, iconOf } from './offerIcons';
import { lu, visible, poser, cumulAt } from './cumulMois';
import {
  listBillingActuals, createBillingActual, updateBillingActual, deleteBillingActual, getCaAdjustment, setCaAdjustmentOffer,
} from '../../api/salesBillingActuals';
import { listOffers } from '../../api/salesOffers';
import { parseSwissAmount, formatSwissAmount } from '../treasury/billing/formatSwiss';
import '../treasury/TreasuryPage.css';
import './Sales.css';

// Onglet « Réalisé » de la section Facturation = SOURCE UNIQUE de saisie de la
// facturation réelle par catégorie libre × 12 mois, par année. Grille façon
// tableur : sélection de plage (clic-glisser / shift-clic), Suppr = vider à 0,
// double-clic/Entrée = éditer, flèches = navigation, Ctrl/Cmd+C = copier, collage
// Excel sur la sélection. Édition en CHF (précision pleine — c'est la source
// de vérité).
// AU FORMAT DES AUTRES PAGES (proto `WIP/protos/facturation-homogene.html`,
// 2026-10-10) : une carte au standard, « ＋ Catégorie » dans l'entête, les
// catégories en pastilles dans la barre d'outils (elles réduisent les lignes
// dans les trois vues), la table RANGÉE PAR OFFRE comme les Prévisions,
// l'année en tête puis les douze mois, un filet à la clôture. Le tri par
// colonne est parti avec ce rangement.
const zeros = () => Array(12).fill(0);
const sum = (arr) => (arr || []).reduce((s, v) => s + (Number(v) || 0), 0);

/* L'UNITÉ DE L'ÉCRAN VAUT AUSSI ICI (2026-10-09) : le réalisé se lit, se
   tape, se colle et se copie en CHF ou en kCHF, comme partout ; en kCHF avec
   une décimale, une facture de quelques centaines de francs ne s'effaçant pas
   à l'arrondi. La base garde des francs. */
const kOf = (unit) => (unit === 'kchf' ? 1000 : 1);
export function fmtUnit(value, unit) {
  if (unit !== 'kchf') return formatSwissAmount(value);
  const n = Number(value) / 1000;
  if (!Number.isFinite(n) || Math.abs(n) < 0.05) return formatSwissAmount(0);
  const [i, d] = Math.abs(n).toFixed(1).split('.');
  return `${n < 0 ? '-' : ''}${i.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${d}`;
}
const toUnit = (v, unit) => Math.round(((Number(v) || 0) / kOf(unit)) * 1e6) / 1e6;

/* LA NATURE de la grille (2026-10-20) : `billing`, la facturation réelle, ou `ca`, le chiffre
   d'affaires réalisé, deux cartes de la section Réalisé qui ne se mélangent jamais. Une ligne se rattache à une
   OFFRE (les catégories de produits d'un fichier chargé, ou tapées) ; le réalisé par client, lui, est le détail des
   Prévisions et ne se saisit pas ici (décision d'Olivier, 2026-10-22). */
export default function BillingActualTab({
  tenantId, year, selCats = null, filtreEntete = null, onDataChanged, addToast, unit = 'chf',
  kind = 'billing', saisie = 'mensuel', onSaisie = null,
}) {
  /* Le mode de SAISIE du CA réalisé : des valeurs mensuelles, ou cumulées depuis janvier (la base garde des montants mensuels). */
  const cumul = kind === 'ca' && saisie === 'cumul';
  const p = useP();
  const rep = useContext(ReportingContext);
  const nav = useContext(ReportNavContext);
  const fmt = (v) => fmtUnit(v, unit);
  const { t } = useTranslation();
  const MONTHS = useMemo(() => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)), [t]);
  const [rows, setRows] = useState([]);
  /* LA FACTURATION SE RATTACHE AUX OFFRES (demande d'Olivier, 2026-09-26) :
     elle est le RÉALISÉ du plan, et sans offre elle ne se compare à rien. Les
     libellés et les montants ne bougent pas, on ajoute seulement à quelle
     offre chaque ligne appartient. Une ligne sans offre reste lisible sous
     « Non rattaché » : ce qui n'est pas rattaché se VOIT, il ne se répartit
     pas en silence. */
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  /* Les offres REPLIÉES (clé d'offre ou 'libres') et le mode « Une ligne à la fois » se RETIENNENT, par client et par donnée :
     on les retrouve en revenant sur la page (demande d'Olivier, 2026-10-22). */
  const [repArr, setRepArr] = usePersistentValueLocal(`sb_repliees_${tenantId}_${kind}`, []);
  const repliees = useMemo(() => new Set(repArr), [repArr]);
  const setRepliees = (f) => setRepArr((prev) => [...(typeof f === 'function' ? f(new Set(prev)) : f)]);
  const [acc, setAcc] = usePersistentValueLocal(`sb_acc_${tenantId}_${kind}`, false);
  /* L'AJUSTEMENT À LA COMPTABILITÉ (2026-10-22) : le CA de la comptabilité, mois clos, et l'offre qui porte l'écart. La ligne
     d'ajustement se CALCULE (comptabilité moins lignes), jamais saisie ; le serveur l'ajoute au réalisé de cette offre. */
  const [compta, setCompta] = useState(null);       // { offer_id, compta: [12] | null }
  const lireCompta = useCallback(() => {
    if (kind !== 'ca' || !tenantId || !year) { setCompta(null); return Promise.resolve(); }
    return getCaAdjustment(tenantId, year)
      .then((r) => setCompta(r && !r.error ? r : null))
      .catch(() => setCompta(null));
  }, [kind, tenantId, year]);
  useEffect(() => { lireCompta(); }, [lireCompta]);
  const [importer, setImporter] = useState(false);      // la fenêtre d'importation d'un fichier ou d'un texte collé

  const [sel, setSel] = useState(null);            // { r1,c1,r2,c2 } indices d'affichage, col 0..11
  const [editing, setEditing] = useState(null);    // { r, c }
  const [editDraft, setEditDraft] = useState('');
  const draggingRef = useRef(false);
  const gridRef = useRef(null);

  const reload = useCallback(async () => {
    if (!tenantId || !year) return;
    const r = await listBillingActuals(tenantId, year, kind);
    if (!r.error) {
      setRows((r.data || []).map((d) => ({
        id: d.id, label: d.label, offer_id: d.offer_id || '', line_id: d.line_id || '',
        monthly_values: Array.from({ length: 12 }, (_, i) => Number(d.monthly_values?.[i]) || 0),
      })));
    }
  }, [tenantId, year, kind]);

  useEffect(() => {
    if (!tenantId || !year) return;
    setLoading(true);
    setSel(null); setEditing(null);
    reload().catch((e) => addToast && addToast(e?.message || t('scenarii:billing.actual.toastLoadError'), 'error'))
      .finally(() => setLoading(false));
  }, [tenantId, year, reload]); // eslint-disable-line

  useEffect(() => {
    if (!tenantId) return;
    listOffers(tenantId).then((r) => setOffers(r.error ? [] : (r.data || []))).catch(() => setOffers([]));
  }, [tenantId]);

  useEffect(() => {
    const up = () => { draggingRef.current = false; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  // Après toute mutation persistée : recharger la grille locale ET prévenir le
  // hub pour rafraîchir 12M / Saisonnalité.
  const afterMutate = useCallback(async () => {
    await reload();
    onDataChanged && onDataChanged();
  }, [reload, onDataChanged]);

  // ── Les lignes affichées : filtrées par les pastilles, rangées par offre ──
  const offerOf = useCallback((id) => offers.find((x) => x.id === id) || null, [offers]);
  const displayRows = useMemo(() => {
    const vues = rows.filter((r) => !selCats || selCats.has(r.id));
    const rank = (r) => (r.offer_id && offers.findIndex((o) => o.id === r.offer_id) >= 0
      ? offers.findIndex((o) => o.id === r.offer_id) : 9999);
    return vues.sort((a, b) => (rank(a) - rank(b)) || (a.label || '').localeCompare(b.label || ''));
  }, [rows, offers, selCats]);

  // ── Sélection ────────────────────────────────────────────────────────────
  const sn = sel ? {
    minR: Math.min(sel.r1, sel.r2), maxR: Math.max(sel.r1, sel.r2),
    minC: Math.min(sel.c1, sel.c2), maxC: Math.max(sel.c1, sel.c2),
  } : null;
  const inSel = (r, c) => sn && r >= sn.minR && r <= sn.maxR && c >= sn.minC && c <= sn.maxC;
  const selCount = sn ? (sn.maxR - sn.minR + 1) * (sn.maxC - sn.minC + 1) : 0;

  function cellDown(e, r, c) {
    if (editing) commitEdit();
    e.preventDefault();
    gridRef.current && gridRef.current.focus();
    if (e.shiftKey && sel) setSel((s) => ({ ...s, r2: r, c2: c }));
    else { setSel({ r1: r, c1: c, r2: r, c2: c }); draggingRef.current = true; }
  }
  function cellEnter(r, c) {
    if (draggingRef.current) setSel((s) => (s ? { ...s, r2: r, c2: c } : s));
  }

  // ── Édition d'une cellule ────────────────────────────────────────────────
  function startEdit(r, c, initial) {
    const row = displayRows[r]; if (!row) return;
    const cur = lu(row.monthly_values, c, cumul);
    setEditing({ r, c });
    setEditDraft(initial != null ? initial : (cur ? String(toUnit(cur, unit)) : ''));
  }
  async function commitEdit() {
    if (!editing) return;
    const { r, c } = editing; const row = displayRows[r];
    setEditing(null);
    if (!row) return;
    const val = Math.round(parseSwissAmount(editDraft) * kOf(unit) * 100) / 100;
    if (lu(row.monthly_values, c, cumul) === val) return;
    const mv = poser(row.monthly_values, c, val, cumul);
    setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, monthly_values: mv } : x)));
    if (row.id) { await updateBillingActual(tenantId, year, row.id, { monthly_values: mv }); onDataChanged && onDataChanged(); }
  }

  // ── TAB AVANCE D'UNE CELLULE (demande d'Olivier, 2026-09-28) ─────────────
  // Sans elle, le navigateur prenait la touche pour lui et sortait de la grille
  // jusqu'à sa barre d'adresse. Tab passe au mois suivant, puis au janvier de
  // la ligne suivante ; Maj+Tab revient en arrière.
  function nextCell(r, c, back) {
    const rmax = displayRows.length - 1;
    if (back) return c > 0 ? { r, c: c - 1 } : (r > 0 ? { r: r - 1, c: 11 } : { r, c });
    return c < 11 ? { r, c: c + 1 } : (r < rmax ? { r: r + 1, c: 0 } : { r, c });
  }
  function tabTo(r, c, back) {
    const n = nextCell(r, c, back);
    setSel({ r1: n.r, c1: n.c, r2: n.r, c2: n.c });
  }

  // ── Clavier (sur la grille focalisée, hors édition) ──────────────────────
  function onGridKeyDown(e) {
    if (editing) return;            // l'input gère ses propres touches
    if (!sn) return;
    const rmax = displayRows.length - 1;
    if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); zeroSelection(); return; }
    if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); startEdit(sel.r2, sel.c2); return; }
    /* COPIER, COUPER, COLLER comme dans un tableur : la copie et le collage passent par les
       événements `copy` et `paste` du navigateur (aucune permission à demander) ; couper copie
       puis vide la sélection ; Ctrl+D recopie la première ligne de la sélection vers le bas. */
    if ((e.metaKey || e.ctrlKey) && (e.key === 'x' || e.key === 'X')) { e.preventDefault(); cutSelection(); return; }
    if ((e.metaKey || e.ctrlKey) && (e.key === 'd' || e.key === 'D')) { e.preventDefault(); fillDown(); return; }
    if (e.key === 'Tab') { e.preventDefault(); tabTo(sel.r2, sel.c2, e.shiftKey); return; }
    if (e.key.startsWith('Arrow')) {
      e.preventDefault();
      let { r2, c2 } = sel;
      if (e.key === 'ArrowUp') r2 = Math.max(0, r2 - 1);
      if (e.key === 'ArrowDown') r2 = Math.min(rmax, r2 + 1);
      if (e.key === 'ArrowLeft') c2 = Math.max(0, c2 - 1);
      if (e.key === 'ArrowRight') c2 = Math.min(11, c2 + 1);
      if (e.shiftKey) setSel((s) => ({ ...s, r2, c2 }));
      else setSel({ r1: r2, c1: c2, r2, c2 });
      return;
    }
    /* La touche OUVRE la cellule avec son chiffre ; sans preventDefault, le
       navigateur la tapait une seconde fois dans le champ qui vient de
       prendre le focus (« 8 » devenait « 88 », 2026-09-30). */
    if (/^[0-9.,\-]$/.test(e.key)) { e.preventDefault(); startEdit(sel.r2, sel.c2, e.key); }
  }

  async function zeroSelection() {
    if (!sn) return;
    const affected = [];
    for (let r = sn.minR; r <= sn.maxR; r++) {
      const row = displayRows[r]; if (!row) continue;
      let mv = [...row.monthly_values]; let changed = false;
      for (let c = sn.minC; c <= sn.maxC; c++) {
        const cible = cumul ? cumulAt(mv, c - 1) : 0;            // vider, c'est ne rien ajouter ce mois-là
        if (lu(mv, c, cumul) !== cible) { mv = poser(mv, c, cible, cumul); changed = true; }
      }
      if (changed) affected.push({ id: row.id, mv });
    }
    if (!affected.length) return;
    setRows((prev) => prev.map((x) => {
      const a = affected.find((z) => z.id === x.id); return a ? { ...x, monthly_values: a.mv } : x;
    }));
    for (const a of affected) await updateBillingActual(tenantId, year, a.id, { monthly_values: a.mv });
    onDataChanged && onDataChanged();
  }

  async function deleteSelectedRows() {
    if (!sn) return;
    const targets = [];
    for (let r = sn.minR; r <= sn.maxR; r++) { const row = displayRows[r]; if (row?.id) targets.push(row); }
    if (!targets.length) return;
    if (!window.confirm(t('scenarii:billing.actual.confirmDeleteRows', { count: targets.length }))) return;
    for (const row of targets) await deleteBillingActual(tenantId, year, row.id);
    setSel(null);
    afterMutate();
  }

  /* Le texte d'une sélection : des lignes, des mois séparés par une tabulation, comme Excel les attend. */
  function selectionText() {
    const lines = [];
    for (let r = sn.minR; r <= sn.maxR; r++) {
      const row = displayRows[r]; const cells = [];
      for (let c = sn.minC; c <= sn.maxC; c++) cells.push(String(toUnit(lu(row.monthly_values, c, cumul), unit)));
      lines.push(cells.join('\t'));
    }
    return lines.join('\n');
  }
  function onGridCopy(e) {
    if (editing || !sn) return;                 // hors édition seulement : l'input copie son texte lui-même
    e.preventDefault();
    e.clipboardData.setData('text/plain', selectionText());
    addToast && addToast(t('scenarii:billing.actual.toastCopied'));
  }
  async function cutSelection() {
    if (!sn) return;
    try { await navigator.clipboard.writeText(selectionText()); } catch (err) { /* sans presse-papiers, on vide quand même */ }
    zeroSelection();
  }
  /* Ctrl+D : la première ligne de la sélection se recopie sur les suivantes, mois par mois. */
  async function fillDown() {
    if (!sn || sn.maxR === sn.minR) return;
    const src = displayRows[sn.minR]; if (!src) return;
    const affected = [];
    for (let r = sn.minR + 1; r <= sn.maxR; r++) {
      const row = displayRows[r]; if (!row) continue;
      let mv = [...row.monthly_values]; let changed = false;
      for (let c = sn.minC; c <= sn.maxC; c++) {
        const v = lu(src.monthly_values, c, cumul);
        if (lu(mv, c, cumul) !== v) { mv = poser(mv, c, v, cumul); changed = true; }
      }
      if (changed) affected.push({ id: row.id, mv });
    }
    if (!affected.length) return;
    setRows((prev) => prev.map((x) => { const a = affected.find((z) => z.id === x.id); return a ? { ...x, monthly_values: a.mv } : x; }));
    for (const a of affected) await updateBillingActual(tenantId, year, a.id, { monthly_values: a.mv });
    onDataChanged && onDataChanged();
  }

  // ── Collage Excel (bloc), ancré sur (anchorRow d'affichage, anchorCol) ────
  // anchorCol = -1 → colonne catégorie (libellé + mois) ; 0..11 → mois.
  async function pasteBlock(e, anchorRow, anchorCol) {
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (!text) return;
    const matrix = text.replace(/\r/g, '').replace(/\n$/, '').split('\n').map((l) => l.split('\t'));
    /* UNE VALEUR SUR UNE SÉLECTION (comme Excel) : une seule cellule collée sur plusieurs cellules
       sélectionnées les remplit toutes. Sur une seule cellule, la saisie reprend la main. */
    if (matrix.length === 1 && matrix[0].length === 1 && anchorCol >= 0) {
      if (!sn || selCount < 2) return;
      e.preventDefault();
      const val = Math.round(parseSwissAmount(matrix[0][0]) * kOf(unit) * 100) / 100;
      const touches = [];
      for (let r = sn.minR; r <= sn.maxR; r++) {
        const row = displayRows[r]; if (!row) continue;
        let mv = [...row.monthly_values];
        for (let c = sn.minC; c <= sn.maxC; c++) mv = poser(mv, c, val, cumul);
        touches.push({ id: row.id, mv });
      }
      setRows((prev) => prev.map((x) => { const a = touches.find((z) => z.id === x.id); return a ? { ...x, monthly_values: a.mv } : x; }));
      for (const a of touches) await updateBillingActual(tenantId, year, a.id, { monthly_values: a.mv });
      onDataChanged && onDataChanged();
      return;
    }
    e.preventDefault();
    const work = displayRows.map((r) => ({ id: r.id, label: r.label, monthly_values: [...r.monthly_values] }));
    for (let r = 0; r < matrix.length; r++) {
      const gi = anchorRow + r;
      let target;
      if (gi < work.length) target = work[gi];
      else { target = { id: null, label: t('scenarii:billing.actual.categoryNumbered', { n: gi + 1 }), monthly_values: zeros() }; work.push(target); }
      for (let c = 0; c < matrix[r].length; c++) {
        const gc = anchorCol + c;
        if (gc === -1) { const lbl = (matrix[r][c] || '').trim(); if (lbl) target.label = lbl; }
        else if (gc >= 0 && gc <= 11) target.monthly_values = poser(target.monthly_values, gc, Math.round(parseSwissAmount(matrix[r][c]) * kOf(unit) * 100) / 100, cumul);
      }
    }
    try {
      for (let i = anchorRow; i < anchorRow + matrix.length && i < work.length; i++) {
        const row = work[i];
        if (row.id) await updateBillingActual(tenantId, year, row.id, { label: row.label, monthly_values: row.monthly_values });
        else await createBillingActual(tenantId, year, { kind, label: row.label, monthly_values: row.monthly_values, display_order: i });
      }
      addToast && addToast(t('scenarii:billing.actual.toastPasted'));
    } catch (err) { addToast && addToast(err?.message || t('scenarii:billing.actual.toastPasteError'), 'error'); }
    afterMutate();
  }

  function onGridPaste(e) {
    if (editing) return;            // l'input d'édition gère son collage
    if (!sn) return;
    pasteBlock(e, sn.minR, sn.minC);
  }

  async function addRow() {
    const r = await createBillingActual(tenantId, year, { kind, label: kind === 'ca' ? p('Réalisé') : t('scenarii:billing.actual.newCategory'), monthly_values: zeros(), display_order: rows.length });
    if (r.error) { addToast && addToast(r.error, 'error'); return; }
    afterMutate();
  }
  async function saveLabel(row, label) {
    const lbl = (label || '').trim() || t('scenarii:billing.actual.categoryFallback');
    setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, label: lbl } : x)));
    if (row.id) { await updateBillingActual(tenantId, year, row.id, { label: lbl }); onDataChanged && onDataChanged(); }
  }

  const grandTotal = displayRows.reduce((s, r) => s + sum(r.monthly_values), 0);
  /* La CLÔTURE coupe l'année : un filet entre le réalisé et ce qui vient. */
  const clos = (() => {
    const c = rep && rep.closed ? String(rep.closed) : null;
    if (!c) return 12;
    const [cy, cm] = c.split('-').map(Number);
    if (cy > year) return 12;
    if (cy < year) return 0;
    return cm;
  })();
  const coupe = clos > 0 && clos < 12;
  const sep = (mi) => (mi === 0 || (coupe && mi === clos) ? ' lk-sep' : '');
  /* Les groupes : une offre et ses catégories, dans l'ordre de `displayRows`
     (la sélection, le clavier et le collage gardent ses index) ; les
     catégories sans offre ferment la table. */
  const groupes = [];
  displayRows.forEach((row, ri) => {
    const cle = row.offer_id && offerOf(row.offer_id) ? row.offer_id : '';
    const g = groupes[groupes.length - 1];
    if (g && g.cle === cle) g.rangs.push({ row, ri });
    else groupes.push({ cle, offre: cle ? offerOf(cle) : null, rangs: [{ row, ri }] });
  });
  /* L'ajustement à la comptabilité : comptabilité moins lignes, mois clos ; l'offre choisie le porte. */
  const avecCompta = kind === 'ca' && Boolean(compta && compta.compta) && clos > 0;
  const lignesTot = MONTHS.map((_, mi) => rows.reduce((a, r) => a + (Number(r.monthly_values?.[mi]) || 0), 0));
  const ajuste = avecCompta ? MONTHS.map((_, mi) => (mi < clos ? Math.round((compta.compta[mi] - lignesTot[mi]) * 100) / 100 : 0)) : null;
  const porteuse = avecCompta ? compta.offer_id : null;
  if (porteuse && offerOf(porteuse) && !groupes.some((g) => g.cle === porteuse)) {
    const g = { cle: porteuse, offre: offerOf(porteuse), rangs: [] };
    const iLibres = groupes.findIndex((x) => !x.cle);
    if (iLibres >= 0) groupes.splice(iLibres, 0, g); else groupes.push(g);
  }
  const choisirPorteuse = async (offerId) => {
    const r = await setCaAdjustmentOffer(tenantId, offerId);
    if (r && r.error) { addToast && addToast(r.error, 'error'); return; }
    await lireCompta();
    onDataChanged && onDataChanged();
  };
  const cleG = (g) => g.cle || 'libres';
  /* « Une ligne à la fois » : ouvrir une offre replie les autres. */
  const basculerG = (g) => setRepliees((x) => {
    if (x.has(cleG(g))) return acc ? new Set(groupes.map(cleG).filter((k) => k !== cleG(g))) : new Set([...x].filter((k) => k !== cleG(g)));
    return new Set([...x, cleG(g)]);
  });
  const basculerAcc = () => {
    if (!acc) {                                   // on l'allume : une seule offre reste ouverte, la première
      const ouverte = groupes.map(cleG).find((k) => !repliees.has(k));
      setRepliees(new Set(groupes.map(cleG).filter((k) => k !== ouverte)));
    }
    setAcc(!acc);
  };
  const toutesRepliees = groupes.length > 0 && groupes.every((g) => repliees.has(cleG(g)));
  const toutBasculer = () => setRepliees(toutesRepliees ? new Set() : new Set(groupes.map(cleG)));
  const moisDe = (rs) => MONTHS.map((_, mi) => rs.reduce((a, x) => a + (Number(x.row.monthly_values[mi]) || 0), 0));
  /* En mode cumulé, les lignes de somme (offre, total) montrent le cumul de leurs mois, sur la même plage que les cellules. */
  const affSommes = (arr) => (cumul ? arr.map((_, i) => (visible(arr, i, true, clos) ? cumulAt(arr, i) : 0)) : arr);

  return (
    <>
    {/* LA RANGÉE AU-DESSUS DE LA TABLE (demande d'Olivier, 2026-10-22) : à gauche le mode de saisie du chiffre d'affaires
        réalisé (des valeurs mensuelles, ou cumulées depuis janvier ; la base garde des mois), à droite Importer et
        Correspondances. L'importation REMPLACE la nature et l'exercice ; c'est aux Correspondances qu'un libellé se
        rattache à une offre. */}
    <div className="sb-barre">
      {kind === 'ca' && onSaisie && (
        <div className="sb-saisie" role="radiogroup" aria-label={p('Mode de saisie')}>
          <span className="sb-saisie__lbl">{p('Mode de saisie')}&nbsp;:</span>
          {[['mensuel', p('Valeurs mensuelles')], ['cumul', p('Valeurs cumulées')]].map(([id, label]) => (
            <label key={id} className="sb-saisie__opt">
              <input type="radio" name="sb-saisie" value={id} checked={saisie === id} onChange={() => onSaisie(id)} />
              {label}
            </label>
          ))}
        </div>
      )}
      <div className="sb-barre__fin">
        <button type="button" className="lk-btn lk-btn--toggle" onClick={() => setImporter(true)}>
          <RiUploadLine aria-hidden="true" />{p('Importer')}
        </button>
        {nav && nav.setPage && (
          <button type="button" className="lk-btn lk-btn--toggle" onClick={() => nav.setPage('sales-source-maps')}>
            {p('Correspondances')}<RiArrowRightUpLine size={13} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
    <CardShell title={kind === 'ca' ? `${p("Chiffre d'affaires réalisé")} ${year}` : `${t('scenarii:billing.title')} ${year}`} flush
               primaryAction={(
                 <>
                   {filtreEntete}
                   <button type="button" className="lk-btn lk-btn--quiet" aria-pressed={acc} onClick={basculerAcc}>{p('Une ligne à la fois')}</button>
                   {(!acc || !toutesRepliees) && (
                     <button type="button" className="lk-btn lk-btn--quiet" onClick={toutBasculer}>
                       {toutesRepliees ? p('Tout déplier') : p('Tout replier')}
                     </button>
                   )}
                   <button type="button" className="lk-btn" onClick={addRow}>＋ {kind === 'ca' ? p('Ligne') : p('Catégorie')}</button>
                 </>
               )}>
      <div className="sales-billing-grid">
        {sn && (
          <div className="sales-bill-toolbar">
            <span>{t('scenarii:billing.actual.selectedCells', { count: selCount })}</span>
            <button type="button" onClick={zeroSelection}>{t('scenarii:billing.actual.zeroSelection')}</button>
            <button type="button" onClick={deleteSelectedRows}>{t('scenarii:billing.actual.deleteRows')}</button>
          </div>
        )}
        {loading && <p className="page-table-loading">{t('scenarii:billing.actual.loading')}</p>}
        {!loading && (
          <div className="sales-bill-scroll lk-hscroll" ref={gridRef} tabIndex={0} onKeyDown={onGridKeyDown} onPaste={onGridPaste} onCopy={onGridCopy}>
            <table className="lk-entry-table sales-bill-table sb-table">
              <colgroup>
                <col style={{ width: 300 }} /><col style={{ width: 92 }} />
                {MONTHS.map((m) => <col key={m} style={{ width: 70 }} />)}
              </colgroup>
              <thead>
                {coupe && (
                  <tr className="sb-band">
                    <th /><th />
                    <th colSpan={clos} className="lk-sep">{p('Réalisé')}</th>
                    <th colSpan={12 - clos} className="lk-sep">{p('À venir')}</th>
                  </tr>
                )}
                <tr>
                  <th className="sb-lbl">{kind === 'ca' ? p('Offre · ligne') : p('Offre · catégorie')}</th>
                  <th className="sb-an">{t('scenarii:billing.actual.total')}<br />{year}</th>
                  {MONTHS.map((m, mi) => <th key={m} className={sep(mi).trim()}>{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {groupes.map((g) => {
                  const porte = Boolean(ajuste) && g.cle === porteuse;
                  const sommes = moisDe(g.rangs).map((v, mi) => (porte ? v + ajuste[mi] : v));
                  const mois = affSommes(sommes);
                  return (
                    <React.Fragment key={g.cle || 'libres'}>
                      <tr className="sb-offre" onClick={() => basculerG(g)}>
                        <td className="sb-lbl">
                          <button type="button" className={`sb-chev${repliees.has(cleG(g)) ? '' : ' is-open'}`}
                                  aria-expanded={!repliees.has(cleG(g))} aria-label={repliees.has(cleG(g)) ? p('Déplier') : p('Replier')}
                                  onClick={(e) => { e.stopPropagation(); basculerG(g); }}>›</button>
                          {g.offre && <OfferIcon name={iconOf(g.offre)} size={15} />}
                          {g.offre ? (g.offre.label || g.offre.name) : t('scenarii:billing.actual.noOffer', 'Non rattaché à une offre')}
                        </td>
                        <td className="sb-an">{fmt(sommes.reduce((a, b) => a + b, 0))}</td>
                        {mois.map((v, mi) => <td key={mi} className={`lk-num${sep(mi)}`}>{v ? fmt(v) : ''}</td>)}
                      </tr>
                      {!repliees.has(cleG(g)) && porte && (
                        <tr className="sb-cat sb-ajust">
                          <td className="sb-lbl">{p('Ajustement à la comptabilité')}</td>
                          <td className="sb-an">{fmt(ajuste.reduce((a, b) => a + b, 0))}</td>
                          {affSommes(ajuste).map((v, mi) => <td key={mi} className={`lk-num${sep(mi)}`}>{mi < clos && v ? fmt(v) : ''}</td>)}
                        </tr>
                      )}
                      {!repliees.has(cleG(g)) && g.rangs.map(({ row, ri }) => (
                        <tr key={row.id || ri} className="sb-cat">
                          <td className="sb-lbl">
                            <button type="button" className="lk-x lk-del sb-del" aria-label={t('scenarii:billing.actual.deleteCategoryTitle')}
                                    onClick={() => { if (window.confirm(t('scenarii:billing.actual.confirmDeleteOne', { label: row.label }))) deleteBillingActual(tenantId, year, row.id).then(afterMutate); }}>✕</button>
                            <input
                              className="sales-bill-label"
                              type="text"
                              value={row.label}
                              onChange={(e) => setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, label: e.target.value } : x)))}
                              onBlur={(e) => saveLabel(row, e.target.value)}
                              onPaste={(e) => pasteBlock(e, ri, -1)}
                            />
                          </td>
                          <td className="sb-an">{fmt(sum(row.monthly_values))}</td>
                          {MONTHS.map((m, mi) => {
                            const isEdit = editing && editing.r === ri && editing.c === mi;
                            const v = visible(row.monthly_values, mi, cumul, clos) ? lu(row.monthly_values, mi, cumul) : 0;
                            return (
                              <td
                                key={mi}
                                className={`sales-bill-mcell${sep(mi)}${inSel(ri, mi) ? ' is-sel' : ''}`}
                                onMouseDown={(e) => !isEdit && cellDown(e, ri, mi)}
                                onMouseEnter={() => cellEnter(ri, mi)}
                                onDoubleClick={() => startEdit(ri, mi)}
                              >
                                {isEdit ? (
                                  <input
                                    className="sales-bill-cell"
                                    type="text"
                                    inputMode="decimal"
                                    autoFocus
                                    value={editDraft}
                                    onChange={(e) => setEditDraft(e.target.value)}
                                    onBlur={commitEdit}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); commitEdit(); setSel({ r1: Math.min(ri + 1, displayRows.length - 1), c1: mi, r2: Math.min(ri + 1, displayRows.length - 1), c2: mi }); }
                                      else if (e.key === 'Escape') { e.preventDefault(); setEditing(null); }
                                      /* La grille reprend le focus : l'input le perd, et
                                         son onBlur enregistre la cellule, une seule fois. */
                                      else if (e.key === 'Tab') { e.preventDefault(); tabTo(ri, mi, e.shiftKey); gridRef.current && gridRef.current.focus(); }
                                    }}
                                  />
                                ) : (
                                  <span className="sales-bill-cell-view">{v ? fmt(v) : ''}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              {(displayRows.length > 0 || avecCompta) && (
                <tfoot>
                  <tr className="sb-total-row">
                    <td className="sb-lbl">{avecCompta ? p('Total saisi / chargé') : t('scenarii:billing.actual.total')}</td>
                    <td className="sb-an">{fmt(grandTotal)}</td>
                    {affSommes(MONTHS.map((m, mi) => displayRows.reduce((a, r) => a + (Number(r.monthly_values?.[mi]) || 0), 0)))
                      .map((v, mi) => <td key={mi} className={sep(mi).trim()}>{fmt(v)}</td>)}
                  </tr>
                  {avecCompta && (
                    <>
                      <tr>
                        <td className="sb-lbl">
                          {p('Ajustement à la comptabilité')}
                          <select className="sb-ajust-offre" value={compta.offer_id || ''} aria-label={p('Offre qui porte l’ajustement')}
                                  onChange={(e) => choisirPorteuse(e.target.value)} disabled={!offers.length}>
                            <option value="">{p('Aucune offre')}</option>
                            {offers.map((o) => <option key={o.id} value={o.id}>{o.label || o.name}</option>)}
                          </select>
                        </td>
                        <td className="sb-an">{fmt(ajuste.reduce((a, b) => a + b, 0))}</td>
                        {affSommes(ajuste).map((v, mi) => <td key={mi} className={`lk-num${sep(mi)}`}>{mi < clos ? fmt(v) : ''}</td>)}
                      </tr>
                      <tr className="sb-total-row">
                        <td className="sb-lbl">{p('Comptabilité')}</td>
                        <td className="sb-an">{fmt(compta.compta.reduce((a, b) => a + b, 0))}</td>
                        {affSommes(compta.compta.map((v, mi) => (mi < clos ? v : 0))).map((v, mi) => <td key={mi} className={`lk-num${sep(mi)}`}>{mi < clos ? fmt(v) : ''}</td>)}
                      </tr>
                    </>
                  )}
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </CardShell>
    {importer && <RealiseImportDialog tenantId={tenantId} year={year} kind={kind} addToast={addToast}
                                      onClose={() => setImporter(false)} onDone={afterMutate} />}
    </>
  );
}
